import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useParams } from "react-router";
import { readModelVersionChecks, type ModelVersionChecks } from "../../api/content/ontologyApi";
import type { ContentVersion } from "../../api/content/workflowsApi";
import type { Decision } from "../../api/access/decisions";
import { useProjects } from "../../projects/projectContext";
import { useLoad, type LoadState } from "../../shared/loading/useLoad";
import StatusMessage from "../../ui/feedback/StatusMessage";
import ReleaseButton from "../knowledgeData/releases/ReleaseButton";
import { releaseDecision } from "../knowledgeData/releases/releasePermission";
import ReleasesMenu from "../knowledgeData/releases/ReleasesMenu";
import { labelOf, useVersionLabels } from "../versions/labels/useVersionLabels";
import { modelVersionName } from "../versions/labels/versionChoiceText";
import VersionChecksChip from "./checks/VersionChecksChip";
import VersionChecksPanel from "./checks/VersionChecksPanel";
import { listModelVersions, resolveModelVersion } from "./modelVersions";
import type { DataModelContext } from "./page/dataModelContext";
import { dataModelPageOfPath, namesOneResource, pageShowsModelVersion } from "./page/dataModelPages";
import ModelVersionPicker from "./page/ModelVersionPicker";

/**
 * The Data model page (F19; the code keeps the name "ontology"), with one navigation level as
 * v1's (F77). The header names the page and the model version once, by its release number,
 * then the version's status: one chip for its checks, which opens their panel beside the
 * terms (design pass section 9). At its right the releases, opened as a list, and "Release…"
 * for those who may release (knowledge-data.md K-12, F2, F17). Below it, the terms, with
 * their kinds as the one row, or the page a link opened, with the way back in its title.
 */
export default function OntologyPage() {
  const { t } = useTranslation();
  const projects = useProjects();
  const projectId = projects.selectedProjectId;
  const { pathname } = useLocation();
  return (
    <div className="flex flex-col gap-4 py-4 lg:h-full">
      {projectId === null ? (
        <>
          <h2 className="app-title text-xl font-semibold">{t("ontologyViewer.title")}</h2>
          {namesOneResource(pathname) ? (
            <Outlet />
          ) : (
            <StatusMessage tone="info" title={t("ontologyViewer.noProjectTitle")}>
              {t("ontologyViewer.noProjectBody")}
            </StatusMessage>
          )}
        </>
      ) : (
        <ProjectDataModel key={projectId} projectId={projectId} releasing={releaseDecision(projects, projectId)} />
      )}
    </div>
  );
}

function ProjectDataModel({ projectId, releasing }: { projectId: string; releasing: Decision }) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { ontologyVersion } = useParams();
  const load = useCallback((signal: AbortSignal) => listModelVersions(projectId, signal), [projectId]);
  const { state: versions, reload: reloadVersions } = useLoad(load);
  const labels = useVersionLabels(projectId, {
    itemVersions: [],
    collectionVersions: versions.status === "loaded" ? versions.value.map((version) => version.reference) : [],
  });
  const page = dataModelPageOfPath(pathname);
  const chosenVersion = ontologyVersion ?? null;
  const shown = page === "terms" && versions.status === "loaded" ? resolveModelVersion(versions.value, chosenVersion) : null;
  const shownCommit = shown?.commit ?? null;
  const loadChecks = useCallback(
    (signal: AbortSignal): Promise<ModelVersionChecks | null> =>
      shownCommit === null ? Promise.resolve(null) : readModelVersionChecks(projectId, shownCommit, signal),
    [projectId, shownCommit],
  );
  const { state: loadedChecks, reload: reloadChecks } = useLoad(loadChecks);
  // Until the shown version's checks arrive, they are loading.
  const checks: LoadState<ModelVersionChecks> =
    loadedChecks.status !== "loaded"
      ? loadedChecks
      : loadedChecks.value === null
        ? { status: "loading" }
        : { status: "loaded", value: loadedChecks.value };
  const [checksOpen, setChecksOpen] = useState(false);
  const versionName = (version: ContentVersion) => modelVersionName(labelOf(labels, version.reference), version, i18n.resolvedLanguage);
  const context: DataModelContext = {
    projectId,
    versions,
    reloadVersions,
    chosenVersion,
    versionName,
    failingTerms: checks.status === "loaded" ? checks.value.failingFocuses : null,
  };
  return (
    <>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* A result or release is a detour: the one Back leaves it (F87, W-11). */}
        <h2 className="app-title text-xl font-semibold">{page === "terms" ? t("ontologyViewer.title") : t(`ontologyViewer.pages.${page}`)}</h2>
        {pageShowsModelVersion(page) && versions.status === "loaded" ? (
          <ModelVersionPicker page={page} versions={versions.value} labels={labels} chosenVersion={chosenVersion} />
        ) : null}
        {shown !== null ? (
          <VersionChecksChip state={checks} open={checksOpen} onToggle={() => { setChecksOpen((open) => !open); }} />
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <ReleasesMenu projectId={projectId} />
          <ReleaseButton projectId={projectId} decision={releasing} />
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Outlet context={context} />
        </div>
        {shown !== null && checksOpen ? (
          <VersionChecksPanel state={checks} onRetry={reloadChecks} onClose={() => { setChecksOpen(false); }} />
        ) : null}
      </div>
    </>
  );
}
